import { cloneElement, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Tooltip for text that the card layout has truncated (line-clamp / truncate).
 *
 * Wraps the clamped element and reveals the full string on hover or keyboard
 * focus. Two deliberate constraints:
 *
 *  - It only opens when the text is *actually* cut off (measured, not guessed),
 *    so a short course name doesn't get a pointless tooltip.
 *  - It renders through a portal with fixed positioning, because the course card
 *    and the Courses panel are both `overflow-hidden` and would clip it.
 *
 * The panel is pointer-events-none: cards are <button>s, and a tooltip that ate
 * the click would break navigation.
 *
 * Takes a single element child and attaches to it directly (no wrapper node), so
 * the measurement happens on the clamped element itself and the card's layout is
 * untouched.
 */

const OPEN_DELAY_MS = 120;
const GAP = 8; // space between trigger and panel
const MARGIN = 8; // min distance from the viewport edge
const MAX_W = 320;

/**
 * `label`   — the tooltip text.
 * `always`  — show whenever the text exists, not only when the trigger is
 *             truncated. Use for icons/badges that have no visible label of
 *             their own (a status icon always deserves its tooltip); leave off
 *             for clamped text, where the tooltip is a fallback for what's cut.
 */
function Tooltip({ label, children, always = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top, left, placement }
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const timerRef = useRef(null);

  const text = String(label || "").trim();

  // Fine pointers only. On touch, hover is emulated by a tap — which is also the
  // card's navigation gesture, so a tooltip there would flash and fight the tap.
  const canHover = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const isTruncated = () => {
    const el = triggerRef.current;
    if (!el) return false;
    // line-clamp overflows vertically; `truncate` overflows horizontally.
    return el.scrollHeight - el.clientHeight > 1 || el.scrollWidth - el.clientWidth > 1;
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const show = useCallback(() => {
    if (!text || (!always && !isTruncated())) return;
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  }, [text, always]);

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
    setPos(null);
  }, []);

  useEffect(() => clearTimer, []);

  // Measure after the panel exists but before paint, so it never flashes at 0,0.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const t = triggerRef.current;
      const p = panelRef.current;
      if (!t || !p) return;
      const r = t.getBoundingClientRect();
      const pw = p.offsetWidth;
      const ph = p.offsetHeight;

      // Prefer the right side; flip to the left when there's no room.
      const toRight = r.right + GAP + pw <= window.innerWidth - MARGIN;
      const left = toRight ? r.right + GAP : r.left - GAP - pw;

      // Centre vertically on the trigger, then clamp inside the viewport.
      const center = r.top + r.height / 2;
      let top = center - ph / 2;
      top = Math.max(MARGIN, Math.min(top, window.innerHeight - ph - MARGIN));

      // Keep the arrow level with the trigger even when the panel got clamped,
      // but never let it slide off the panel's own rounded corners.
      const arrow = Math.max(12, Math.min(center - top, ph - 12));

      setPos({ top, left, arrow, placement: toRight ? "right" : "left" });
    };
    place();
    window.addEventListener("resize", place);
    // Capture: the Courses panel scrolls internally, not just the window.
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Close on Escape / scroll-away is handled above; this covers tab-out.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") hide();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, hide]);

  const trigger = cloneElement(children, {
    ref: triggerRef,
    onPointerEnter: (e) => {
      children.props.onPointerEnter?.(e);
      if (e.pointerType === "mouse" && canHover()) show();
    },
    onPointerLeave: (e) => {
      children.props.onPointerLeave?.(e);
      hide();
    },
    onFocus: (e) => {
      children.props.onFocus?.(e);
      show();
    },
    onBlur: (e) => {
      children.props.onBlur?.(e);
      hide();
    },
  });

  // Theme-based surface — a soft tint of the community's brand colour on the
  // card surface, with a matching brand-tinted border and pointer. Falls back to
  // a neutral blue when no theme colour is configured. The base of the mix is
  // the surface TOKEN, not a literal white: in dark mode a white-based tint left
  // the panel bright while its text followed the ramp and went pale, so the
  // tooltip read as an empty box.
  const TIP_BG =
    "color-mix(in srgb, var(--brand-primary, #0274F7) 7%, rgb(var(--c-surface-raised)))";
  const TIP_BORDER = "color-mix(in srgb, var(--brand-primary, #0274F7) 30%, transparent)";
  // Solid (dark) brand colour for the pointer so it reads as a themed marker.
  const TIP_ARROW = "var(--brand-primary, #0274F7)";

  return (
    <>
      {trigger}

      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="tooltip"
            style={{
              top: pos ? pos.top : 0,
              left: pos ? pos.left : 0,
              maxWidth: MAX_W,
              background: TIP_BG,
              borderColor: TIP_BORDER,
              // Hidden until measured — avoids a first-paint jump at 0,0.
              visibility: pos ? "visible" : "hidden",
            }}
            // break-words is load-bearing. Text wraps at SPACES only, so a
            // description typed without any (or a pasted URL / id) has no break
            // opportunity: the panel blew straight past maxWidth and off the side
            // of the window. Worse, the placement maths above reads offsetWidth,
            // which stays clamped at MAX_W — so the flip-to-left never triggered
            // and the overflow ran off-screen unflipped.
            className="pointer-events-none fixed z-[10000] break-words rounded-xl border px-3.5 py-2.5 text-[12.5px] font-medium leading-relaxed text-slate-700 shadow-[0_14px_36px_-10px_rgba(15,23,42,0.35)] backdrop-blur-sm animate-[fadeIn_120ms_ease-out]"
          >
            {text}
            <span
              aria-hidden="true"
              style={{
                top: pos ? pos.arrow : "50%",
                [pos?.placement === "left" ? "right" : "left"]: -9,
                borderTop: "12px solid transparent",
                borderBottom: "12px solid transparent",
                ...(pos?.placement === "left"
                  ? { borderLeft: `9px solid ${TIP_ARROW}` }
                  : { borderRight: `9px solid ${TIP_ARROW}` }),
              }}
              className="absolute h-0 w-0 -translate-y-1/2"
            />
          </div>,
          document.body
        )}
    </>
  );
}

export default Tooltip;
